import { Category } from '~/api/services/loginPopups'
import i18n from '~/components/i18n'

export enum FieldType {
  Input = 'input',
  Select = 'select',
  Date = 'date',
  CITY = 'city-input',
  MOBILE = 'mobile-input',
  PHONE = 'phone-input',
  BIRTH_DATE = 'birth-date',
  STREET = 'street-input',
  CANTON = 'canton-select',
  COUNTRY = 'country-select',
  IBAN = 'iban-input',
  CONTACT_FIRST_NAME = 'contact-first-name-input',
  CONTACT_LAST_NAME = 'contact-last-name-input',
  BANK_ACCOUNT_HOLDER = 'bank-account-holder-input'
}

export type CategoryField = {
  key: string | string[]
  slug: string
  action: number
  label: string
  placeholder: string
  fieldRequired?: boolean
  type: FieldType
  info?: string
}

export type CategoryFields = Record<Category, CategoryField[][]>

export const GRID_COLUMNS = 3

export const READONLY_COLUMNS = 5

export const categoryIcons: Record<Category, string> = {
  [Category.UserData]: 'icon-avatar',
  [Category.ManagerData]: 'icon-business-card',
  [Category.FarmerData]: 'icon-farmer-icon'
}

export const SINGLE_INPUT_ARRAY_TYPES = new Set<FieldType>([
  FieldType.MOBILE,
  FieldType.PHONE,
  FieldType.IBAN
])

export const allCategoryFields = (): CategoryFields => ({
  [Category.UserData]: [
    [{
      key: 'firstName',
      slug: 'first_name',
      action: 2,
      label: i18n.t('First name'),
      placeholder: i18n.t('First name'),
      type: FieldType.Input,
      info: i18n.t('First name of the person according to the official document'),
      fieldRequired: true
    },
    {
      key: 'lastName',
      slug: 'last_name',
      action: 2,
      label: i18n.t('Last name'),
      placeholder: i18n.t('Last name'),
      type: FieldType.Input,
      info: i18n.t('Last name of the person according to the official document'),
      fieldRequired: true
    },
    {
      key: 'birthDate',
      slug: 'birth_date',
      action: 0,
      label: i18n.t('Date of birth'),
      placeholder: i18n.t('Date'),
      type: FieldType.BIRTH_DATE,
      info: i18n.t('Date of birth in the format DD.MM.YYYY (e.g. 15.03.1980)'),
      fieldRequired: true
    }
    ],
    [
      {
        key: 'email',
        slug: 'email',
        action: 1,
        label: i18n.t('Email address'),
        placeholder: i18n.t('Email address'),
        type: FieldType.Input,
        info: i18n.t('Valid e-mail address for notifications and login (e.g. max.muster@beispiel.ch)')
      },
      {
        key: ['profile.mobileNumber', 'profile.mobilePrefix'],
        slug: 'mobile_number',
        action: 1,
        label: i18n.t('Mobile number'),
        placeholder: i18n.t('Mobile number'),
        type: FieldType.Input,
        info: i18n.t('Mobile number in international format (e.g. +41 79 123 45 67)')
      },
      {

        key: 'no-field',
        slug: 'no-field',
        action: 0,
        label: i18n.t('Email address'),
        placeholder: i18n.t('Email address'),
        type: FieldType.Input,
        info: i18n.t('Valid e-mail address for notifications and login (e.g. max.muster@beispiel.ch)')
      }
    ]
  ],

  [Category.ManagerData]: [
    [
      {
        key: 'agisId',
        slug: 'agis_id',
        action: 1,
        label: i18n.t('AGIS-ID person'),
        placeholder: i18n.t('Please enter AGIS-ID'),
        type: FieldType.Input,
        info: i18n.t('Unique person identifier from the AGIS system (assigned automatically)')
      },
      {
        key: 'firstName',
        slug: 'first_name',
        action: 2,
        label: i18n.t('First name'),
        placeholder: i18n.t('First name'),
        type: FieldType.Input,
        info: i18n.t('First name of the manager according to the official document'),
        fieldRequired: true
      },
      {
        key: 'lastName',
        slug: 'last_name',
        action: 2,
        label: i18n.t('Last name'),
        placeholder: i18n.t('Last name'),
        type: FieldType.Input,
        info: i18n.t('Last name of the manager according to the official document'),
        fieldRequired: true
      }
    ]
    ,
    [{
      key: ['address.streetName', 'address.streetNumber'],
      slug: 'street_name',
      action: 1,
      label: i18n.t('Street'),
      placeholder: i18n.t('Enter street'),
      type: FieldType.STREET,
      info: i18n.t('Street and house number of the main address (e.g. Musterstrasse 12a)'),
      fieldRequired: true
    },
    {
      key: ['address.zip', 'address.city'],
      slug: 'city',
      action: 1,
      label: i18n.t('City'),
      placeholder: i18n.t('Enter city'),
      type: FieldType.CITY,
      info: i18n.t('Four-digit postal code and town name (e.g. 3000 Bern)'),
      fieldRequired: true
    }]
    ,
    [{
      key: 'burNumber',
      slug: 'bur_number',
      action: 0,
      label: i18n.t('BUR Number'),
      placeholder: i18n.t('Enter BUR Number'),
      type: FieldType.Input,
      info: i18n.t('Business unit register number of the Federal Statistical Office (8 digits)')
    },
    {
      key: 'address.cantonId',
      slug: 'canton',
      action: 1,
      label: i18n.t('Canton'),
      placeholder: i18n.t('Canton'),
      type: FieldType.CANTON,
      info: i18n.t('Canton abbreviation (e.g. BE, ZH, AG)')
    },
    {
      key: 'address.countryId',
      slug: 'country',
      action: 1,
      label: i18n.t('Country'),
      placeholder: i18n.t('Country'),
      type: FieldType.COUNTRY,
      info: i18n.t('Country of the address (e.g. Switzerland)')
    }]
    ,
    [{
      key: 'financialSettings[0].bankAccountHolder',
      slug: 'bank_account_holder',
      action: 1,
      label: i18n.t('Bank account holder'),
      placeholder: i18n.t('Please enter account holder'),
      type: FieldType.BANK_ACCOUNT_HOLDER,
      info: i18n.t('Name of account holder / beneficiary for payments')
    },
    {
      key: 'financialSettings[0].iban',
      slug: 'iban',
      action: 1,
      label: i18n.t('IBAN'),
      placeholder: i18n.t('Please enter IBAN'),
      type: FieldType.IBAN,
      info: i18n.t('International bank account number for payments (e.g. CH56 0483 5012 3456 7800 9)')
    }
    ]
  ],

  [Category.FarmerData]: [
    [
      {
        key: 'agisId',
        slug: 'agis_id',
        action: 1,
        label: i18n.t('AGIS-ID operation'),
        placeholder: i18n.t('Please enter AGIS-ID operation'),
        type: FieldType.Input,
        info: i18n.t('Unique farm identifier from the AGIS system (assigned automatically)')
      },
      {
        key: 'evsNumber',
        slug: 'evs_number',
        action: 1,
        label: i18n.t('EVS number'),
        placeholder: i18n.t('EVS number'),
        type: FieldType.Input,
        info: i18n.t('Unique EVS farm number (assigned automatically)')
      },
      {
        key: 'companyName',
        slug: 'company_name',
        action: 1,
        label: i18n.t('Company name'),
        placeholder: i18n.t('Company name'),
        type: FieldType.Input,
        info: i18n.t('Official name of the farm (e.g. Landwirtschaftsbetrieb Muster GmbH)'),
        fieldRequired: true
      }
    ],

    [
      {
        key: ['address.streetName', 'address.streetNumber'],
        slug: 'street_name',
        action: 1,
        label: i18n.t('Street'),
        placeholder: i18n.t('Enter street'),
        type: FieldType.STREET,
        info: i18n.t('Street and house number of the main address (e.g. Musterstrasse 12a)'),
        fieldRequired: true
      },
      {
        key: ['address.zip', 'address.city'],
        slug: 'city',
        action: 1,
        label: i18n.t('City'),
        placeholder: i18n.t('Enter city'),
        type: FieldType.CITY,
        info: i18n.t('Four-digit postal code and town name (e.g. 3000 Bern)'),
        fieldRequired: true
      }
    ],
    [
      {
        key: 'longitude',
        slug: 'longitude',
        action: 2,
        label: i18n.t('Longitude'),
        placeholder: i18n.t('Please enter longitude'),
        type: FieldType.Input,
        info: i18n.t('Geographic longitude of the farm location (e.g. 7.447)')
      },
      {
        key: 'latitude',
        slug: 'latitude',
        action: 2,
        label: i18n.t('Latitude'),
        placeholder: i18n.t('Please enter latitude'),
        type: FieldType.Input,
        info: i18n.t('Geographic latitude of the farm location (e.g. 46.948)')
      },
      {
        key: 'address.cantonId',
        slug: 'canton',
        action: 1,
        label: i18n.t('Canton'),
        placeholder: i18n.t('Canton'),
        type: FieldType.CANTON,
        info: i18n.t('Canton abbreviation (e.g. BE, ZH, AG)')
      },
      {
        key: 'address.countryId',
        slug: 'country',
        action: 1,
        label: i18n.t('Country'),
        placeholder: i18n.t('Country'),
        type: FieldType.COUNTRY,
        info: i18n.t('Country of the address (e.g. Switzerland)')
      }
    ],

    [
      {
        key: 'contacts[0].firstName',
        slug: 'contact_first_name',
        action: 1,
        label: i18n.t('Contact first name'),
        placeholder: i18n.t('Enter Contact first name'),
        type: FieldType.CONTACT_FIRST_NAME,
        info: i18n.t('First name of the farm\'s contact person')
      },
      {
        key: 'contacts[0].lastName',
        slug: 'contact_last_name',
        action: 0,
        label: i18n.t('Contact Last Name'),
        placeholder: i18n.t('Contact Last Name'),
        type: FieldType.CONTACT_LAST_NAME,
        info: i18n.t('Last name of the farm\'s contact person')
      }
    ],
    [
      {
        key: 'contacts[0].mobileNumber',
        slug: 'contact_mobile_number',
        action: 0,
        label: i18n.t('Mobile number'),
        placeholder: i18n.t('Mobile number'),
        type: FieldType.Input,
        info: i18n.t('Mobile number in international format (e.g. +41 79 123 45 67)')
      },
      {
        key: 'contacts[0].phoneNumber',
        slug: 'contact_phone_number',
        action: 0,
        label: i18n.t('Phone number'),
        placeholder: i18n.t('Phone number'),
        type: FieldType.Input,
        info: i18n.t('Landline number in international format (e.g. +41 31 123 45 67)')
      },
      {
        key: 'contacts[0].email',
        slug: 'contact_email',
        action: 0,
        label: i18n.t('Email'),
        placeholder: i18n.t('Email'),
        type: FieldType.Input,
        info: i18n.t('Valid e-mail address for notifications and login (e.g. max.muster@beispiel.ch)')
      }
    ]
  ]
})